import { useEffect, useMemo, useState } from "react";
import { Container, Card, Button, Alert, Spinner, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom"; // keep navigate, remove id
import { client } from "../../lib/AmplifyClient";
import type { Schema } from "../../../amplify/data/resource";
import { useUserContext } from "../Context/UserContext";

type Profile = Schema["Profile"]["type"];

const DPI_TYPES = [
  "ISTJ","ISFJ","INFJ","INTJ",
  "ISTP","ISFP","INFP","INTP",
  "ESTP","ESFP","ENFP","ENTP",
  "ESTJ","ESFJ","ENFJ","ENTJ",
];

function coerceStrings(arr?: (string | null)[]): string[] {
  return (arr ?? []).filter((s): s is string => typeof s === "string" && s.trim().length > 0);
}

const mustHaveSub = (sub?: string | null) => {
  if (!sub) throw new Error("No Cognito sub found. Please re-login.");
};

function unwrap<T>(label: string, res: any): T | null {
  if (res && typeof res === "object" && "data" in res) {
    const { data, errors } = res as { data?: T | null; errors?: any[] };
    if (errors?.length) {
      console.error(`[DPITest][${label}][ERRORS]`, errors);
      return null;
    }
    return (data ?? null) as T | null;
  }
  return (res as T) ?? null;
}

export default function DPITestComponent() {
  const { sub, firstName, email } = useUserContext();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const [dpiSelected, setDpiSelected] = useState<string | null>(null);
  const [dpiSaving, setDpiSaving] = useState(false);
  const [dpiSaved, setDpiSaved] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const displayName = useMemo(() => (firstName || email || "there") as string, [firstName, email]);

  /** Profile (viewer) CRUD */
  async function readProfile(sub: string): Promise<Profile | null> {
    const res = await (client as any).models.Profile.get({ id: sub }, { authMode: "apiKey" });
    return unwrap<Profile>("READ get", res);
  }
  async function ensureProfile(sub: string, e?: string | null) {
    const existing = await readProfile(sub);
    if (existing) return existing;
    const cr = await (client as any).models.Profile.create(
      { id: sub, ...(e ? { email: e } : {}) },
      { authMode: "apiKey" }
    );
    const created = unwrap<Profile>("WRITE create", cr);
    if (!created) throw new Error("Create failed.");
    return created;
  }
  async function updateProfile(sub: string, fields: Partial<Profile>): Promise<Profile> {
    const up = await (client as any).models.Profile.update(
      { id: sub, ...fields },
      { authMode: "apiKey" }
    );
    const updated = unwrap<Profile>("WRITE update", up);
    if (!updated) throw new Error("Update returned null.");
    return updated;
  }

  /** Initial load */
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        mustHaveSub(sub);
        console.log("[DPITest][BOOT] load profile", { sub });

        const p = await readProfile(sub!);
        if (!mounted) return;

        setProfile(p ?? null);

        const existingDpi = p?.dpiType?.toString().trim() || "";
        if (existingDpi) { setDpiSelected(existingDpi); setDpiSaved(true); }

        console.log("[DPITest][READ][initial]", {
          found: !!p, id: (p as any)?.id, email: (p as any)?.email,
          dpiType: existingDpi
        });
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message || "Failed to load your profile.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [sub]);

  /** Save DPI */
  const handleSubmitDPI = async () => {
    try {
      mustHaveSub(sub);
      if (!dpiSelected) return;

      setDpiSaving(true);
      setError(null);

      console.log("[DPITest][DPI][SUBMIT]", { sub, email, dpiSelected });

      await ensureProfile(sub!, email);

      const prevResults = coerceStrings((profile as any)?.dpiResults as (string | null)[] | undefined);
      const nextResults = [...prevResults, "test"];

      const updated = await updateProfile(sub!, { dpiType: dpiSelected, dpiResults: nextResults });
      console.log("[DPITest][DPI][WRITE OK]", {
        id: (updated as any)?.id, dpiType: (updated as any)?.dpiType, dpiResults: (updated as any)?.dpiResults
      });
      setProfile(updated);

      const rb = await readProfile(sub!);
      console.log("[DPITest][READ-BACK][after DPI]", {
        id: (rb as any)?.id, dpiType: (rb as any)?.dpiType
      });

      const ok = (rb?.dpiType?.toString().trim() || "") === dpiSelected;
      setDpiSaved(ok);

      // Optional: navigate somewhere after save (e.g., back to Home)
      // navigate('/');
    } catch (e: any) {
      console.error("[DPITest][DPI][ERROR]", e?.errors ?? e?.message ?? e);
      setError(e?.message || "Failed to save your DPI type.");
    } finally {
      setDpiSaving(false);
    }
  };

  const skyBlue = "#87CEEB";
  const skyBlueActive = "#5ec0ea";
  const darkBlue = "#0d47a1";
  const textDark = "#002b36";
  const slate = "#eceff1";
  const googleBlue = "#4285F4";

  if (loading) {
    return (
      <Container className="my-4 text-center">
        <Spinner animation="border" role="status" />
        <div className="mt-3">Loading your profile…</div>
      </Container>
    );
  }

  return (
    <Container className="my-4">
      <Card className="mx-auto" style={{ maxWidth: 980 }}>
        <Card.Body>
          <h3 className="mb-2">Quick Setup</h3>
          <div className="text-muted mb-4">
            {`Hi ${displayName}, pick your personality type.`}
          </div>

          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

          {/* DPI */}
          <Card className="mb-2" style={{ background: slate }}>
            <Card.Body>
              <h5 className="mb-2">Choose Your DPI Type</h5>
              <div className="text-muted mb-3">Select the four-letter type that best fits you.</div>
              <Row xs={2} sm={3} md={4} className="g-2">
                {DPI_TYPES.map(t => {
                  const isActive = dpiSelected === t;
                  return (
                    <Col key={t}>
                      <Button
                        className="w-100"
                        onClick={() => { if (!dpiSaved) { setDpiSelected(t); setError(null); } }}
                        disabled={dpiSaved}
                        style={{
                          backgroundColor: isActive ? skyBlueActive : skyBlue,
                          borderColor: isActive ? skyBlueActive : skyBlue,
                          color: textDark, fontWeight: 700, letterSpacing: 0.5,
                          opacity: dpiSaved ? 0.75 : 1,
                          cursor: dpiSaved ? "not-allowed" as const : "pointer",
                        }}
                      >
                        {t}
                      </Button>
                    </Col>
                  );
                })}
              </Row>
              <div className="d-flex justify-content-end mt-3">
                <Button
                  onClick={handleSubmitDPI}
                  disabled={!dpiSelected || dpiSaving || dpiSaved}
                  style={{ backgroundColor: darkBlue, borderColor: darkBlue, paddingLeft: "1.25rem", paddingRight: "1.25rem" }}
                >
                  {dpiSaved ? "Saved" : dpiSaving ? "Saving…" : "Submit"}
                </Button>
              </div>
            </Card.Body>
          </Card>

          <div className="text-muted mt-3">
            Your DPI type is saved to your profile.
          </div>

          {/* Home button */}
          <div className="d-flex justify-content-center mt-4">
            <Button
              onClick={() => navigate("/")}
              style={{
                backgroundColor: googleBlue,
                borderColor: googleBlue,
                paddingLeft: "1.5rem",
                paddingRight: "1.5rem",
                fontWeight: 600,
              }}
            >
              Home
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}
